import React, { useEffect, useState } from 'react';
import { GeoMapperData, GeoMapperItem } from '../types/geo-mapper.types';
import { Dialog, DialogContent } from 'src/components/ui/dialog';
import { LeafletVanillaMap } from './geo-mapper-mapbox';
import { useGetGeoMapperItemQuery } from '../endpoints/geo-mapper.endpoints';

interface GeoMapperTableProps {
  items: GeoMapperItem[];
  isLoading?: boolean;
  onItemClick?: (item: GeoMapperItem) => void;
  className?: string;
  showEmptyState?: boolean;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  } else {
    return num.toString();
  }
};

const TableHeader: React.FC = () => (
  <thead className="bg-gray-50">
    <tr>
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-80 lg:w-96">
        Display Name
      </th>
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        Original Filename
      </th>
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        Row Count
      </th>
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        Created At
      </th>
    </tr>
  </thead>
);

const TableRow: React.FC<{
  item: GeoMapperItem;
  onClick?: () => void;
}> = ({ item, onClick }) => (
  <tr
    className="hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-200"
    onClick={onClick}
  >
    <td className="px-6 py-4 w-80 lg:w-96">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-gray-900 truncate">
          {item.display_name || (
            <span className='text-gray-500'>
              Empty
            </span>
          )}
        </div>
      </div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
      <div
        className="max-w-xs truncate"
        title={item.original_filename}
      >
        {item.original_filename}
      </div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
      {formatNumber(item.row_count)}
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
      {formatDate(item.created_at)}
    </td>
  </tr>
);

const GeoMapperTable: React.FC<GeoMapperTableProps> = ({
  items,
  isLoading = false,
  onItemClick,
  className = '',
  showEmptyState = true,
}) => {
  const [selectedGeoMapperItem, setSelectedGeoMapperItem] = useState<GeoMapperItem | null>(null);
  const [showMapBox, setShowMapBox] = useState(false);

  const { data: geoMapperItem, isLoading: isLoadingGeoMapperItem, isError: isErrorGeoMapperItem, refetch: refetchGeoMapperItem } = useGetGeoMapperItemQuery(
    {id: selectedGeoMapperItem?.id || ''});

  useEffect(() => {
    if (selectedGeoMapperItem && geoMapperItem) {
      setShowMapBox(true);
    } else {
      setShowMapBox(false);
    }
  }, [selectedGeoMapperItem, geoMapperItem]);

  return (
    <>
      <div className={`bg-white rounded-lg shadow overflow-hidden relative ${className}`}>
        {/* Loading Mask */}
        {isLoading && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-800 mb-1">
                  Loading
                </p>
                <p className="text-sm text-gray-600">
                  Fetching data...
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="overflow-x-auto max-h-[600px]">
          <table className="min-w-full divide-y divide-gray-200 table-fixed">
            <TableHeader />
            <tbody className="bg-white divide-y divide-gray-200">
              {items.length === 0 && !isLoading && showEmptyState ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                        <svg
                          className="w-6 h-6 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      <p className="text-sm font-medium text-gray-900">
                        {showEmptyState ? 'No files uploaded' : 'No results found'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {showEmptyState ? 'Upload a CSV file to get started' : 'Try adjusting your search terms'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <TableRow
                    key={item.id}
                    item={item}
                    onClick={() => {
                      setSelectedGeoMapperItem(item);
                      geoMapperItem && setShowMapBox(true);
                    }}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Dialog
        open={showMapBox}
        onOpenChange={setShowMapBox}
      >
        <DialogContent
          className='bg-white w-3/4 h-3/4 max-w-full max-h-full m-6 p-0 focus:outline-none overflow-hidden'
          title="Geo Mapper Map View"
          tabIndex={-1}
        >
          <LeafletVanillaMap
            geoData={geoMapperItem as unknown as GeoMapperData}
            dotSize={6}
            showLabels
            showLegend
            grayscaleTiles={false}
            style={{ height: '100%' }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GeoMapperTable;
